import { useEffect, useRef, useState } from "react";
import { mediaSrc, mediaSrcSet, placeholderSrc } from "@/lib/img";

/**
 * Drop-in <img> that asks the media API for a right-sized WebP variant and paints a tiny
 * blurred preview of the same photo behind it, so a slow image never leaves a dark hole.
 * `width` is the largest CSS width the image is painted at.
 */
export default function Img({ src, width = 1280, sizes, eager = false, style, className = "", ...rest }) {
  const ref = useRef(null);
  const [loaded, setLoaded] = useState(false);

  // images restored from cache can finish before React attaches onLoad
  useEffect(() => {
    setLoaded(Boolean(ref.current?.complete && ref.current.naturalWidth));
  }, [src]);

  if (!src) return null;

  // a cover-filling preview would sit behind a logo like a coloured box, so marks fade instead
  const preview = className.includes("object-contain") ? null : placeholderSrc(src);
  const state = `${preview ? "img-blur" : "img-fade"}${loaded ? " is-loaded" : ""}`;

  return (
    <img
      ref={ref}
      src={mediaSrc(src, width)}
      srcSet={mediaSrcSet(src, width)}
      sizes={sizes || `${width}px`}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      onLoad={() => setLoaded(true)}
      onError={() => setLoaded(true)}
      data-loaded={loaded ? "true" : "false"}
      className={`${state} ${className}`}
      style={{
        ...style,
        ...(preview && !loaded
          ? {
              backgroundImage: `url("${preview}")`,
              backgroundSize: "cover",
              backgroundPosition: style?.objectPosition || "center",
              backgroundRepeat: "no-repeat",
            }
          : null),
      }}
      {...rest}
    />
  );
}
